import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import dayjs from 'dayjs';
import { not, or } from 'ember-truth-helpers';
import type { Platform } from 'frontend-utils/utils/constants/constant-values';
import { DOMAIN_NAME } from 'frontend-utils/utils/constants/domain-utils';
import { getLocalTime } from 'frontend-utils/utils/dayjs';
import generateOtaLink from 'frontend-utils/utils/url/generate-ota-link';
import GeneralParityIcons from 'lumen/components/general/parity-icons';
import HotelTooltipsTooltipRatesTable from 'lumen/components/hotel/tooltips/tooltip-rates-table';
import ParityOtaParityLink from 'lumen/components/parity/ota-parity-link';
import parityClass from 'lumen/helpers/parity-class';
import type { Period } from 'lumen/models';
import type { PeriodParity } from 'lumen/models/period';
import type AjaxService from 'lumen/services/ajax';
import type {
  HotelAndMarketServiceWithHotel,
  HotelOrMarket,
} from 'lumen/services/hotel-and-market';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import type {
  MealTypeValues,
  MembershipType,
  RateParam,
} from 'lumen/utils/constants/general-constants';
import {
  RATE_PARAMS,
  RATETYPES,
} from 'lumen/utils/constants/general-constants';
import { formatUrl } from 'lumen/utils/rates-request';

export interface TdParityColumn {
  hotel: HotelOrMarket | null;
  isModal?: boolean;
  isParityAnalytics: boolean;
  isParityOtaOutOfSync?: boolean;
  otaId?: string;
  pos?: string;
  showMemberRate: boolean;
}

interface ParityTablesCellsTdParitySignature {
  Args: {
    cellData: Period;
    column: TdParityColumn;
  };
}

export default class ParityTablesCellsTdParity extends Component<ParityTablesCellsTdParitySignature> {
  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare liveupdate: Services['liveupdate'];
  @service declare rates: Services['rates'];

  @service declare router: Services['router'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get day() {
    return this.period.day;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotel() {
    // Hotel is passed as an argument in market/parity/insight/historical
    return this.args.column.hotel ?? this.hotelAndMarketService.hotel;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMissing() {
    return this.parityObject?.message === 'general.missing';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPast() {
    if (!this.day.date) {
      return false;
    }
    return this.day.date.isBefore(
      getLocalTime(`${this.hotel.hotelinfo.timezone}`),
      'day',
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isToday() {
    if (!this.day.date) {
      return false;
    }

    return this.day.date.isSame(
      getLocalTime(`${this.hotel.hotelinfo.timezone}`),
      'day',
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isUpdating() {
    if (this.args.column.pos) {
      return this.liveupdate.isPOSParityUpdating(
        this.period.fromDate,
        this.day.date,
        this.period.parities || [],
        this.args.column.pos,
      );
    }

    return this.liveupdate.isDefaultParityUpdating(
      this.period.fromDate,
      this.day.date,
      this.period.parities || [],
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get los() {
    return Number(
      // @ts-expect-error: Object is possibly 'null'.
      this.router.currentRoute.queryParams['los'] ??
        DEFAULT_QP_VALUES.application.los,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get maxPersons() {
    return Number(
      // @ts-expect-error: Object is possibly 'null'.
      this.router.currentRoute.queryParams['maxPersons'] ??
        DEFAULT_QP_VALUES.application.maxPersons,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get mealPlan() {
    return Number(
      // @ts-expect-error: Object is possibly 'null'.
      this.router.currentRoute.queryParams['mealPlan'] ??
        DEFAULT_QP_VALUES.application.mealPlan,
    ) as MealTypeValues;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get membershiptype() {
    return Number(
      // @ts-expect-error: Object is possibly 'null'.
      this.router.currentRoute.queryParams['membershipType'] ??
        DEFAULT_QP_VALUES.application.membershipType,
    ) as MembershipType;
  }

  /**
   * Parity data for the current cell based on the OTA of the column
   * It's an object when the column is for only one OTA
   * It's an array when the column is for multiple OTAs
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get parityDataPerOta() {
    if (this.period.paritiesDict && this.args.column.otaId) {
      return this.period.paritiesDict[this.args.column.otaId] as
        | PeriodParity
        | PeriodParity[];
    }
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get parityObject() {
    return this.parityDataPerOta as PeriodParity;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get period() {
    return this.args.cellData;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get platform() {
    return Number(
      // @ts-expect-error: Object is possibly 'null'.
      this.router.currentRoute.queryParams['platform'] ??
        DEFAULT_QP_VALUES.application.platform,
    ) as Platform;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rateParam() {
    return (
      // @ts-expect-error: Object is possibly 'null'.
      (this.router.currentRoute.queryParams['rateParam'] as RateParam) ??
      DEFAULT_QP_VALUES.application.rateParam
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rateValue() {
    return this.rates.formatRate(
      this.parityDataPerOta as PeriodParity,
      this.hotel,
      { longFormat: false },
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showOTAParityLink() {
    const message = this.parityObject?.message;

    return (
      !this.isPast &&
      !this.args.column.isParityAnalytics &&
      typeof message === 'string' &&
      message !== 'general.missing' &&
      message.indexOf('rates.meal.missing') === -1
    );
  }

  // quick admin only way to check to full rate object of the cell
  @action onDoubleClick(): void {
    if (this.access.isStaffUser && console) {
      console.group(
        `Debugging info: ${this.parityObject?.channel} - ${this.day?.date.format('ddd l')}`,
      );

      if (console.table) {
        console.table(this.parityObject);
      }

      const strConsoleLayout = 'color: #005EA0; font-weight: bold;';

      const hotelId = this.hotel?.id;
      const hotelInfoId = this.hotel?.hotelinfo?.id;
      const selectedOTA = this.parityObject?.ota;

      console.log(
        `%cHotelInfo: ${this.ajax.host}/admin/hotel_industry/hotelinfo/${hotelInfoId}/change/`,
        strConsoleLayout,
      );

      if (this.period && this.parityObject?.extract_datetime && selectedOTA) {
        // @ts-expect-error: Object is possibly 'null'.
        const fromDate = this.period.id.split(',')[0];

        // Conversion to local extract date happening here! WHY?
        // Newintegrities work with a local extract date timestamp, while the API still uses UTC extract dates.
        // We cannot simply use toLocalTime in hotelAndMarketService, because it rounds the UTC timestamp down T00:00:00, and then adds hours, which makes no change in some cases
        // So instead we take the UTC timestamp and preserve the hours, and we add the timezone to it in hours, the resulting day is the local extract date timestamp
        const extractDateUTC = dayjs.utc(this.parityObject?.extract_datetime);
        const timezone = this.hotelAndMarketService.timezone;
        const extractDateLocal = extractDateUTC

          .add(timezone, 'hour')
          .format('YYYY-MM-DD');
        const currentTab = 'parity';
        const scrapedDataURL = encodeURI(
          `${this.ajax.host}/redirect/scraped/?ota=${selectedOTA}&from_date=${fromDate}&hotelinfo_id=${hotelInfoId}&hotel_id=${hotelId}&extract_date=${extractDateLocal}&is_new_integrities=true&los=${this.los}&type=${currentTab}`,
        );
        const fullsURL = encodeURI(
          `${this.ajax.host}/redirect/fulls/?ota=${selectedOTA}&from_date=${fromDate}&hotelinfo_id=${hotelInfoId}&hotel_id=${hotelId}&extract_date=${extractDateLocal}&is_new_integrities=true&los=${this.los}&type=${currentTab}`,
        );
        const ratesURL = encodeURI(
          `${this.ajax.host}/redirect/rates/?ota=${selectedOTA}&from_date=${fromDate}&hotelinfo_id=${hotelInfoId}&hotel_id=${hotelId}&extract_date=${extractDateLocal}&type=${currentTab}`,
        );

        const multiViewParams = {
          ota: selectedOTA,
          los: this.los,
          persons: this.maxPersons,
          membershiptype: this.membershiptype,
          from_date_range_start: fromDate,
          from_date_range_end: fromDate,
          hotel_id: hotelId,
          'hotelinfo_ids[]': hotelInfoId,
          meta: 'nested',
          type: currentTab,
          platform: this.platform,
          rate_type:
            this.rateParam === RATE_PARAMS.THIRD_PARTY
              ? RATETYPES.THIRD_PARTY
              : RATETYPES.NONE,
        };
        const multiViewURL = encodeURI(
          formatUrl(`https://viewer.${DOMAIN_NAME}/multiview`, multiViewParams),
        );

        const taxConfigParams = {
          subscription_id: hotelId,
          currency: this.parityObject?.currency,
          shop_currency: this.parityObject?.shop_currency,
          value: this.parityObject?.value,
          shop_value: this.parityObject?.shop_value,
          vat: this.parityObject?.vat,
          vat_incl: this.parityObject?.vat_incl,
          city_tax: this.parityObject?.city_tax,
          city_tax_incl: this.parityObject?.city_tax_incl,
          other_taxes: this.parityObject?.other_taxes,
          other_taxes_incl: this.parityObject?.other_taxes_incl,
          shop_vat_incl: this.parityObject?.shop_vat_incl,
          shop_other_taxes_incl: this.parityObject?.shop_other_taxes_incl,
          shop_city_tax_incl: this.parityObject?.shop_city_tax_incl,
          room_name: this.parityObject?.room_name,
          cancellation: this.parityObject?.best_flex, // Should be updated to bar in the future (see OTA-7604)
          extract_datetime: this.parityObject?.extract_datetime,
          max_persons: this.parityObject?.max_persons,
          mealtype_included: this.parityObject?.mealtype_included,
          membershiptype: this.parityObject?.membershiptype,
          ota: this.parityObject?.ota,
          hotelinfo: this.parityObject?.hotelinfo,
          type: currentTab,
          live_url: encodeURIComponent(
            generateOtaLink(
              selectedOTA,
              // @ts-expect-error: Argument of type 'string | null' is not assignable to parameter of type 'string | number'.
              hotelInfoId,
              hotelId,
              this.hotel.isTripadvisorRebranding,
              this.period,
              this.maxPersons,
              '',
              undefined,
            ),
          ),
        };

        const taxConfigUri = encodeURI(
          formatUrl(
            '/admin/matching/taxconfig/taxconfig_calculation/',
            taxConfigParams,
            this.ajax.host,
          ),
        );

        console.log(`%cScraped data: ${scrapedDataURL}`, strConsoleLayout);
        console.log(`%cFulls: ${fullsURL}`, strConsoleLayout);
        console.log(`%cRates: ${ratesURL}`, strConsoleLayout);
        console.log(`%cMultiview: ${multiViewURL}`, strConsoleLayout);

        let taxConfigMessage = taxConfigUri;
        const oneHourAgo = dayjs.utc().subtract(1, 'hour');
        if (extractDateUTC && extractDateUTC.isBefore(oneHourAgo)) {
          taxConfigMessage = 'only available after performing a live shop';
        }

        console.log(`%cTax configs: ${taxConfigMessage}`, strConsoleLayout);
      } else {
        console.log('value has no period or extract_datetime');
      }

      console.groupEnd();
    }
  }

  <template>
    {{! template-lint-disable no-invalid-interactive }}
    <div class="truncate tabular-nums" {{on "dblclick" this.onDoubleClick}}>
      <span
        class="flex items-center justify-end
          {{if
            (not (or this.isUpdating @column.isParityOtaOutOfSync))
            (parityClass this.parityObject)
          }}
          {{if this.isToday 'td-today'}}"
        role="tooltip"
      >
        {{#if this.showOTAParityLink}}
          {{#if this.parityObject}}
            <ParityOtaParityLink
              @hotel={{this.hotel}}
              @ota={{this.parityObject.ota}}
              @period={{this.period}}
              @persons={{this.maxPersons}}
              @value={{this.rateValue}}
            />
          {{/if}}
        {{else}}
          {{this.rateValue}}
        {{/if}}
        <GeneralParityIcons
          @mealType={{this.mealPlan}}
          @parity={{this.parityObject}}
          @rateParam={{this.rateParam}}
          @showMemberRate={{@column.showMemberRate}}
        />
      </span>
      {{#unless this.isMissing}}
        <HotelTooltipsTooltipRatesTable
          @date={{this.day.date}}
          @displayOverModal={{@column.isModal}}
          @fetchOtherLosRates={{false}}
          @hotel={{this.hotel}}
          @isParityAnalytics={{@column.isParityAnalytics}}
          @isUpdating={{this.isUpdating}}
          @value={{this.parityObject}}
        />
      {{/unless}}
    </div>
  </template>
}
